import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { zipcodesAPI, bookkeepersAPI } from '../services/api';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import './BookkeepersByZipcode.css';

const BookkeepersByZipcode = () => {
  const { zipCode } = useParams();
  const [bookkeepers, setBookkeepers] = useState([]);
  const [statusBreakdown, setStatusBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchBookkeepersByZipcode();
  }, [zipCode, statusFilter, currentPage]);

  const fetchBookkeepersByZipcode = async () => {
    try {
      setLoading(true);
      const params = { 
        status: statusFilter === 'all' ? null : statusFilter,
        page: currentPage,
        limit: 10
      };
      
      const response = await zipcodesAPI.getBookkeepersByZipcode(zipCode, params);
      
      if (response.data.success) {
        setBookkeepers(response.data.data.bookkeepers);
        setStatusBreakdown(response.data.data.statusBreakdown);
        setTotalPages(response.data.data.pagination.total);
      }
    } catch (error) {
      console.error('Error fetching bookkeepers by zipcode:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (bookkeeper) => {
    if (bookkeeper.status === 'rejected' || !bookkeeper.isActive) {
      return <span className="status-badge rejected">Rejected</span>;
    }
    
    if (bookkeeper.expirationDate && new Date(bookkeeper.expirationDate) < new Date()) {
      return <span className="status-badge expired">Expired</span>;
    }
    
    if (bookkeeper.status === 'pending') {
      return <span className="status-badge pending">Pending</span>;
    }
    
    return <span className="status-badge approved">Active</span>;
  };

  const handleStatusChange = async (bookkeeperId, newStatus) => {
    try {
      if (newStatus === 'approve') {
        await bookkeepersAPI.approve(bookkeeperId, { durationMonths: 12 });
      } else if (newStatus === 'reject') {
        await bookkeepersAPI.reject(bookkeeperId, { reason: 'Manual rejection from zipcode view' });
      }
      
      fetchBookkeepersByZipcode(); // Refresh data
    } catch (error) {
      console.error('Error updating bookkeeper status:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="bookkeepers-by-zipcode">
      <div className="page-header">
        <Link to="/zipcodes" className="back-link">← Back to Zipcodes</Link>
        <h1>Bookkeepers in {zipCode}</h1>
        <p>Manage bookkeepers for this geographic area</p>
      </div>

      {statusBreakdown && (
        <div className="zipcode-overview">
          <div className="overview-stats">
            <div className="overview-stat">
              <span className="stat-number">{statusBreakdown.total}</span>
              <span className="stat-label">Total</span>
            </div>
            <div className="overview-stat approved">
              <span className="stat-number">{statusBreakdown.approved}</span>
              <span className="stat-label">Active</span>
            </div>
            <div className="overview-stat pending">
              <span className="stat-number">{statusBreakdown.pending}</span>
              <span className="stat-label">Pending</span>
            </div>
            <div className="overview-stat expired">
              <span className="stat-number">{statusBreakdown.expired}</span>
              <span className="stat-label">Expired/Rejected</span>
            </div>
          </div>
        </div>
      )}

      <div className="filters-section">
        <div className="status-filters">
          <button 
            className={`filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            All ({statusBreakdown?.total || 0})
          </button>
          <button 
            className={`filter-btn approved ${statusFilter === 'active' ? 'active' : ''}`}
            onClick={() => setStatusFilter('active')}
          >
            Active ({statusBreakdown?.approved || 0})
          </button>
          <button 
            className={`filter-btn pending ${statusFilter === 'pending' ? 'active' : ''}`}
            onClick={() => setStatusFilter('pending')}
          >
            Pending ({statusBreakdown?.pending || 0})
          </button>
          <button 
            className={`filter-btn expired ${statusFilter === 'expired' ? 'active' : ''}`}
            onClick={() => setStatusFilter('expired')}
          >
            Expired ({statusBreakdown?.expired || 0})
          </button>
        </div>
      </div>

      <div className="bookkeepers-table-container">
        <table className="bookkeepers-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Company</th>
              <th>Contact</th>
              <th>Location</th>
              <th>Status</th>
              <th>Expiration</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {bookkeepers.map((bookkeeper) => (
              <tr key={bookkeeper._id} className="bookkeeper-row">
                <td className="name-cell">
                  <strong>{bookkeeper.fullName}</strong>
                </td>
                <td className="company-cell">
                  {bookkeeper.companyName || 'N/A'}
                </td>
                <td className="contact-cell">
                  <div className="contact-info">
                    {bookkeeper.email && (
                      <div className="email">{bookkeeper.email}</div>
                    )}
                    {bookkeeper.phone && (
                      <div className="phone">{bookkeeper.phone}</div>
                    )}
                  </div>
                </td>
                <td className="location-cell">
                  {bookkeeper.city}, {bookkeeper.state}
                </td>
                <td className="status-cell">
                  {getStatusBadge(bookkeeper)}
                </td>
                <td className="expiration-cell">
                  {bookkeeper.expirationDate ? formatDate(bookkeeper.expirationDate) : 'N/A'}
                </td>
                <td className="actions-cell">
                  <div className="action-buttons">
                    {bookkeeper.status === 'pending' && (
                      <>
                        <button 
                          className="btn-approve"
                          onClick={() => handleStatusChange(bookkeeper._id, 'approve')}
                        >
                          Approve
                        </button>
                        <button 
                          className="btn-reject"
                          onClick={() => handleStatusChange(bookkeeper._id, 'reject')}
                        >
                          Reject
                        </button>
                      </>
                    )}
                    {bookkeeper.status === 'approved' && bookkeeper.isActive && (
                      <button 
                        className="btn-extend"
                        onClick={() => handleStatusChange(bookkeeper._id, 'extend')}
                      >
                        Extend
                      </button>
                    )}
                    <Link 
                      to={`/bookkeepers/${bookkeeper._id}`}
                      className="btn-view"
                    >
                      View
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {bookkeepers.length === 0 && (
          <div className="no-results">
            <p>No bookkeepers found for zipcode {zipCode} with the current filters.</p>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button 
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
            className="pagination-btn"
          >
            Previous
          </button>
          
          <span className="page-info">
            Page {currentPage} of {totalPages}
          </span>
          
          <button 
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default BookkeepersByZipcode;